import React from 'react';
import { Grid, Paper, Typography, Box, Tooltip } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SavingsIcon from '@mui/icons-material/Savings';
import InfoIcon from '@mui/icons-material/Info';

const SummaryCards = ({ balanceSheetItems, cashFlowItems }) => {
  // Calculate net worth
  const assets = balanceSheetItems
    .filter(item => item.type === 'asset')
    .reduce((sum, item) => sum + (item.amount || 0), 0);
  
  const liabilities = balanceSheetItems
    .filter(item => item.type === 'liability')
    .reduce((sum, item) => sum + (item.amount || 0), 0);
  
  const netWorth = assets - liabilities;

  // Frequency multipliers for monthly and annual calculations
  const monthlyMultipliers = {
    'one-time': 0,
    'daily': 30,
    'weekly': 4.33,  // More accurate weekly to monthly conversion
    'fortnightly': 2.17,  // More accurate fortnightly to monthly conversion
    'monthly': 1,
    'quarterly': 1/3,
    'annually': 1/12
  };

  const annualMultipliers = {
    'one-time': 0,
    'daily': 365,
    'weekly': 52,
    'fortnightly': 26,
    'monthly': 12,
    'quarterly': 4,
    'annually': 1
  };

  // Calculate monthly income and expenses
  const monthlyIncome = cashFlowItems
    .filter(item => item.type === 'income')
    .reduce((sum, item) => {
      return sum + (item.amount * monthlyMultipliers[item.frequency] || 0);
    }, 0);

  const monthlyExpenses = cashFlowItems
    .filter(item => item.type === 'expense')
    .reduce((sum, item) => {
      return sum + (item.amount * monthlyMultipliers[item.frequency] || 0);
    }, 0);

  const monthlyNetIncome = monthlyIncome - monthlyExpenses;

  // Calculate annual income and expenses
  const annualIncome = cashFlowItems
    .filter(item => item.type === 'income')
    .reduce((sum, item) => {
      return sum + (item.amount * annualMultipliers[item.frequency] || 0);
    }, 0);

  const annualExpenses = cashFlowItems
    .filter(item => item.type === 'expense')
    .reduce((sum, item) => {
      return sum + (item.amount * annualMultipliers[item.frequency] || 0);
    }, 0);

  const annualNetIncome = annualIncome - annualExpenses;

  // Calculate savings rate
  const savingsRate = monthlyIncome > 0 
    ? ((monthlyIncome - monthlyExpenses) / monthlyIncome * 100).toFixed(1)
    : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const cards = [
    {
      title: 'Net Worth',
      value: formatCurrency(netWorth),
      icon: <AccountBalanceIcon sx={{ fontSize: 40, color: netWorth >= 0 ? 'success.main' : 'error.main' }} />,
      color: netWorth >= 0 ? 'success.main' : 'error.main',
      tooltip: 'Total assets minus total liabilities'
    },
    {
      title: 'Monthly Net Income',
      value: formatCurrency(monthlyNetIncome),
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: monthlyNetIncome >= 0 ? 'success.main' : 'error.main' }} />,
      color: monthlyNetIncome >= 0 ? 'success.main' : 'error.main',
      tooltip: 'Monthly income minus monthly expenses, with all amounts converted to monthly values based on frequency'
    },
    {
      title: 'Annual Net Income',
      value: formatCurrency(annualNetIncome),
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: annualNetIncome >= 0 ? 'success.main' : 'error.main' }} />,
      color: annualNetIncome >= 0 ? 'success.main' : 'error.main',
      tooltip: 'Annual income minus annual expenses, with all amounts converted to annual values based on frequency'
    },
    {
      title: 'Monthly Savings Rate',
      value: `${savingsRate}%`,
      icon: <SavingsIcon sx={{ fontSize: 40, color: savingsRate >= 20 ? 'success.main' : 'warning.main' }} />,
      color: savingsRate >= 20 ? 'success.main' : 'warning.main',
      tooltip: 'Percentage of monthly income that is saved (Monthly Income - Monthly Expenses) ÷ Monthly Income × 100'
    }
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: '100%',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: card.color,
                opacity: 0.8,
              }
            }}
          >
            <Box sx={{ 
              mb: 2,
              p: 2,
              borderRadius: '50%',
              backgroundColor: (theme) => `${card.color}${theme.palette.mode === 'dark' ? '30' : '15'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {card.icon}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {card.title}
              </Typography>
              <Tooltip title={card.tooltip} arrow>
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ 
                color: card.color, 
                fontWeight: 'bold',
                letterSpacing: '-0.5px'
              }}
            >
              {card.value}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default SummaryCards; 